import { useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, ExternalLink } from "lucide-react";
import Nav from "@/components/landing/Nav";
import { APP_NAME } from "@/constants/site";

const CONTACT_EMAIL = "info@mcstreamline.co.uk";
const GOOGLE_PARTNER_URL = "https://policies.google.com/technologies/partner-sites";

const Section = ({ number, title, children, testId }) => (
  <motion.section
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    className="border-t border-white/10 pt-10"
    data-testid={testId}
  >
    <p className="font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-crimson">{number}</p>
    <h2 className="mt-3 font-heading text-xl font-bold tracking-tight text-white sm:text-2xl">{title}</h2>
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-slate-400 sm:text-base">{children}</div>
  </motion.section>
);

const EmailLink = () => (
  <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-white underline decoration-crimson/60 underline-offset-4 transition-colors hover:text-crimson" data-testid="privacy-contact-email-link">
    {CONTACT_EMAIL}
  </a>
);

const GoogleLink = () => (
  <a
    href={GOOGLE_PARTNER_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 font-semibold text-white underline decoration-crimson/60 underline-offset-4 transition-colors hover:text-crimson"
    data-testid="google-partner-sites-link"
  >
    How Google uses information from sites and apps that use its services
    <ExternalLink size={13} />
  </a>
);

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="spotlight relative min-h-screen" data-testid="privacy-policy-page">
      <div className="noise-overlay" />
      <Nav />
      <main className="relative mx-auto max-w-3xl px-6 pb-28 pt-36 sm:pt-40">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-crimson">Legal</p>
          <h1 className="mt-4 font-heading text-4xl font-black tracking-tight text-white sm:text-5xl" data-testid="privacy-policy-heading">
            Privacy Policy
          </h1>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500" data-testid="privacy-last-updated">
            Last updated: 31 October 2026
          </p>
          <p className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg">
            This policy explains, in plain English, how {APP_NAME} — our iOS app and this website — handles your information. No legal jargon, no surprises. If anything is unclear, just email us at <EmailLink />.
          </p>
        </motion.div>

        <div className="mt-14 space-y-12">
          <Section number="01" title="The information we collect" testId="privacy-section-collect">
            <p>We only collect what we need to run the app and make it work well for you. That falls into three groups:</p>
            <ul className="list-disc space-y-3 pl-5">
              <li>
                <span className="font-semibold text-slate-200">Account details.</span> When you create an account we collect things like your name, email address, and username, so we can set up and secure your profile.
              </li>
              <li>
                <span className="font-semibold text-slate-200">Content you upload.</span> Videos, photos, comments, likes, and anything else you choose to post or share on the platform.
              </li>
              <li>
                <span className="font-semibold text-slate-200">Device information.</span> Technical details such as your device model, operating system version, app version, and device identifiers (including your advertising ID). This helps us fix bugs, keep the app secure, and show ads.
              </li>
            </ul>
          </Section>

          <Section number="02" title="How we use your information" testId="privacy-section-use">
            <p>We use the information above to run and improve the app: keeping your account working, showing your content to the people you share it with, personalising your feed, fixing problems, keeping the community safe, and — if you allow it — showing you advertising.</p>
            <p>We do not use your information for anything unrelated, and we never sell your personal information for money.</p>
          </Section>

          <Section number="03" title="How long we keep your data" testId="privacy-section-retention">
            <p>We keep your information for as long as your account is active, or for as long as we need it to provide the service. When information is no longer needed, we delete it or anonymise it so it can no longer be linked to you.</p>
            <p>If you ask us to delete your data, we aim to complete the request within 30 days. Copies held in our secure backups may take a little longer to disappear completely, but they are not used for anything in the meantime.</p>
          </Section>

          <Section number="04" title="Advertising and Google AdMob" testId="privacy-section-advertising">
            <p>The app shows ads provided by <span className="font-semibold text-slate-200">Google AdMob</span>. To show ads and measure how well they work, AdMob and Google's advertising partners may collect and use device identifiers (such as your advertising ID), your approximate location, and information about how you use the app.</p>
            <p>Depending on the choice you make, the ads you see may be <span className="font-semibold text-slate-200">personalised</span> (based on your activity and interests) or <span className="font-semibold text-slate-200">non-personalised</span> (not based on a profile of you). Non-personalised ads still use limited information — like your approximate location and the content you are viewing — to work properly.</p>
            <p>You can read more here: <GoogleLink />.</p>
          </Section>

          <Section number="05" title="Your choices in the UK and Europe" testId="privacy-section-uk-eu">
            <p>If you are in the United Kingdom or the European Economic Area, you will see a consent message when you first open the app. It lets you choose whether to allow personalised advertising.</p>
            <p>You can <span className="font-semibold text-slate-200">accept</span>, <span className="font-semibold text-slate-200">decline</span>, or <span className="font-semibold text-slate-200">change your choice at any time</span> through the privacy options in the app. Whatever you choose, you can keep using the app — declining personalised ads simply means the ads you see will be non-personalised.</p>
          </Section>

          <Section number="06" title="Your rights in the United States" testId="privacy-section-us">
            <p>This section applies if you live in the United States, including California and other states with their own privacy laws.</p>
            <ul className="list-disc space-y-3 pl-5">
              <li>
                <span className="font-semibold text-slate-200">We do not sell your personal information for money.</span> Full stop.
              </li>
              <li>
                <span className="font-semibold text-slate-200">Advertising and "sharing".</span> Some state laws treat the sharing of data for personalised advertising as "sharing" or "targeted advertising", even though no money changes hands. The advertising data described in the AdMob section above may count as this kind of sharing under those laws.
              </li>
              <li>
                <span className="font-semibold text-slate-200">Opting out.</span> You can opt out of personalised ads at any time using the privacy options message in the app.
              </li>
              <li>
                <span className="font-semibold text-slate-200">See, correct or delete.</span> You can ask to see the personal information we hold about you, ask us to correct it, or ask us to delete it, by emailing <EmailLink />.
              </li>
              <li>
                <span className="font-semibold text-slate-200">No unfair treatment.</span> We will never treat you differently or unfairly — in price, features, or service — for using any of these rights.
              </li>
            </ul>
          </Section>

          <Section number="07" title="Tracking choices on iPhone" testId="privacy-section-iphone">
            <p>On iPhone, you are in control of tracking. When the app first asks, Apple's tracking prompt lets you <span className="font-semibold text-slate-200">allow</span> or <span className="font-semibold text-slate-200">refuse</span> tracking across other companies' apps and websites.</p>
            <p>You can change your mind whenever you like in your phone's settings: go to <span className="font-semibold text-slate-200">Settings → Privacy &amp; Security → Tracking</span> and toggle {APP_NAME} on or off.</p>
            <p>For more detail on how our advertising partner handles data, see: <GoogleLink />.</p>
          </Section>

          <Section number="08" title="Children under 13" testId="privacy-section-children">
            <p>{APP_NAME} is not aimed at children under 13, and we do not knowingly collect personal information from them. If you believe a child under 13 has given us their information, please email <EmailLink /> and we will remove it promptly.</p>
          </Section>

          <Section number="09" title="Asking us to delete your data" testId="privacy-section-deletion">
            <p>You can ask us to delete your account and personal data at any time. Simply email <EmailLink /> with the subject line "Delete my data" from the email address linked to your account, and we will take care of it. We aim to complete deletion requests within 30 days.</p>
          </Section>

          <Section number="10" title="Changes to this policy" testId="privacy-section-changes">
            <p>If we change this policy, we will update the "Last updated" date at the top of this page. If the changes are significant, we will let you know in the app as well.</p>
          </Section>

          <Section number="11" title="How to contact us" testId="privacy-section-contact">
            <p>Questions, requests, or concerns about your privacy? We are happy to help.</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="group inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.04] px-5 py-3 transition-colors duration-300 hover:border-crimson/40 hover:bg-crimson/10"
              data-testid="privacy-contact-button"
            >
              <Mail size={16} className="text-crimson" />
              <span className="font-heading text-sm font-semibold text-white sm:text-base">{CONTACT_EMAIL}</span>
            </a>
          </Section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
